'use client';

import { ImageLoaderProps } from 'next/image';

import { CardButton } from '../../../components/Cards/CardButton';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { FishCatchesIcon } from '../../../components/Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../../components/NextImage/NextImageWrapper';
import { getCatchCardEventProperties } from '../../../components/PostsViewer/helpers/getCatchCardEventProperties';
import { openPostViewerEventSources } from '../../../components/PostsViewer/types/openPostViewerEventSources';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { CatchFieldsForPostsViewer } from '../../../generated/graphql';
import { EmptyCatchCardImage } from '../../Explore/components/MapDrawer/EmptyCatchCardImage/EmptyCatchCardImage';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getCatchImageAlt } from '@domain/catches/getCatchImageAlt';
import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { getPageCatches } from '@graphql/queries/rutilus/Page';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { usePostViewer } from '@domain/posts/usePostViewer';
import { PostsViewer } from '../../../components/PostsViewer/PostsViewer';
import { PageCatchesGridLoader } from './PageCatchesGridLoader';

interface IProps {
  pageId: string;
}

export const PageCatchesGrid = ({ pageId }: IProps) => {
  const { currentPage } = useAnalytics();
  const clientHasRendered = useClientHasRendered();

  const {
    currentPostViewerId,
    closePostViewer,
    isPostViewerOpen,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer();

  const { data: catchesData, loading: isLoading } = useRutilusQuery(getPageCatches, {
    variables: { id: pageId },
  });

  const catchPostList = getNodesFromConnection<CatchFieldsForPostsViewer>(
    catchesData?.entry.catches,
  ).map(_catch => _catch.post);

  if (!isLoading && clientHasRendered && catchPostList.length === 0) {
    return <></>;
  }

  return (
    <div>
      <Heading variant="sm">Catches</Heading>
      {isLoading || !clientHasRendered ? (
        <PageCatchesGridLoader />
      ) : (
        <Grid>
          {catchPostList.map(post => {
            const image = post.images.edges?.[0]?.node;
            const imageUrl = image?.url ?? '';
            const imageWidth = image?.width ?? 0;
            const imageHeight = image?.height;

            const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
              getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

            return (
              <CardButton
                className="relative col-span-2 pb-[100%] sm:col-span-3"
                data-testid={`recent-catch-${post._id}`}
                id={post._id}
                key={post._id}
                eventProperties={getCatchCardEventProperties({
                  postId: post._id,
                  currentPage,
                  source: 'recently-logged-catches',
                })}
                onClick={() =>
                  void openPostViewer(post._id, openPostViewerEventSources.PagesCatches)
                }
                ariaLabel="recent catch in this fishing water"
              >
                {imageUrl !== '' ? (
                  <NextImageWrapper
                    className="size-full object-cover"
                    wrapperClassName="size-full static"
                    customPlaceholderIcon={<FishCatchesIcon size={100} title="fish catches icon" />}
                    alt={getCatchImageAlt(post.catch?.species?.displayName)}
                    src={imageUrl}
                    loader={imgProxyLoader}
                    fill
                    sizes="(min-width: 1680px) 385px,(min-width: 600px) 25vw, 50vw"
                  />
                ) : (
                  <EmptyCatchCardImage className="absolute inset-y-0 right-0 rounded-none border-none" />
                )}
              </CardButton>
            );
          })}
        </Grid>
      )}
      {catchPostList.length > 0 && isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={catchPostList}
          currentPostViewerId={currentPostViewerId}
          hasMore={false}
          isPostViewerOpen
          loadMore={(): void => {}}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </div>
  );
};
